
import { formatDate } from "date-fns";
import { useEffect, useRef, useState } from "react";
import { useCheckJobStatusQuery } from "../../../../features/_baseQuery";
import { useGetCompaniesCSVMutation } from "../../../../features/companyApi";
import { useGetPeopleCSVMutation } from "../../../../features/peopleApi";
import { addBannerWithTimeout } from "../../../lib/centralizedBanner/slice";
import { ContactType } from "../../../lib/filters/dto";
import { useAppDispatch } from "../../../lib/redux/hooks";
import { downloadCSVfromBase64 } from "../../../lib/utils";

export function useDownloadCsv(selectedIds: string[], setSelectedIds: (ids: string[]) => void, contactType: ContactType) {
    const dispatch = useAppDispatch();

    const [getPeopleCSV, {
        isLoading: isGettingPeopleCSV,
        reset: resetMutationBulk
    }] = useGetPeopleCSVMutation();

    const [getCompaniesCSV, {
        isLoading: isGettingCompaniesCSV,
        reset: resetMutationCompanies
    }] = useGetCompaniesCSVMutation();

    const [jobIdCompanies, setJobIdCompanies] = useState<string | null>(null);
    const [isPollingCompanies, setIsPollingCompanies] = useState(false);
    const [jobIdPeople, setJobIdPeople] = useState<string | null>(null);
    const [isPollingPeople, setIsPollingPeople] = useState(false);
    const processedJobIds = useRef(new Set<string>());

    const isLoading = isGettingPeopleCSV || isGettingCompaniesCSV || isPollingCompanies || isPollingPeople;

    const { data: jobStatusCompanies, isError: isPollingErrorCompanies } = useCheckJobStatusQuery(jobIdCompanies!, {
        skip: !jobIdCompanies || !isPollingCompanies,
        pollingInterval: 4000,
    });

    const { data: jobStatusPeople, isError: isPollingErrorPeople } = useCheckJobStatusQuery(jobIdPeople!, {
        skip: !jobIdPeople || !isPollingPeople,
        pollingInterval: 4000,
    });

    useEffect(() => {
        return () => {
            resetMutationBulk();
            resetMutationCompanies();
            setIsPollingCompanies(false);
            setJobIdCompanies(null);
            setIsPollingPeople(false);
            setJobIdPeople(null);
            processedJobIds.current.clear();
        };
    }, [resetMutationBulk, resetMutationCompanies]);

    const handleCloseDownloadModal = () => {
        setSelectedIds([]);
        resetMutationBulk();
        resetMutationCompanies();
    };

    useEffect(() => {
        if (jobStatusCompanies && (jobStatusCompanies.status === 'COMPLETED' || jobStatusCompanies.status === 'FAILED')) {
            if (processedJobIds.current.has(jobStatusCompanies.id)) {
                return;
            }

            processedJobIds.current.add(jobStatusCompanies.id);

            setIsPollingCompanies(false);
            setJobIdCompanies(null);

            if (jobStatusCompanies.status === 'COMPLETED') {
                let formattedResponse: string = ""

                if (jobStatusCompanies.resultJson) {
                    try {
                        const parsedResults = JSON.parse(jobStatusCompanies.resultJson);
                        formattedResponse = parsedResults;
                    } catch (e) {
                        console.error('Errore nel parsing dei risultati:', e);
                    }
                }

                downloadCSVfromBase64(formattedResponse, "cd_companies_download_" + formatDate(new Date(), "dd_MM_yyyy"))
                setSelectedIds([]);
                handleCloseDownloadModal();
                dispatch(addBannerWithTimeout({
                    message: "File CSV generato con successo",
                    variant: "success"
                }));


            } else {
                dispatch(addBannerWithTimeout({
                    message: jobStatusCompanies.errorMessage || "Si è verificato un errore durante l'importazione",
                    variant: "error"
                }));
            }
        } else if (isPollingErrorCompanies) {
            setIsPollingCompanies(false);
            setJobIdCompanies(null);
            dispatch(addBannerWithTimeout({
                message: "Si è verificato un errore imprevisto durante il polling dello stato del job",
                variant: "error"
            }));
        }
    }, [jobStatusCompanies, isPollingErrorCompanies, dispatch]);

    useEffect(() => {
        if (jobStatusPeople && (jobStatusPeople.status === 'COMPLETED' || jobStatusPeople.status === 'FAILED')) {
            if (processedJobIds.current.has(jobStatusPeople.id)) {
                return;
            }

            processedJobIds.current.add(jobStatusPeople.id);

            setIsPollingPeople(false);
            setJobIdPeople(null);

            if (jobStatusPeople.status === 'COMPLETED') {
                let csvBase64: string = ""

                if (jobStatusPeople.resultJson) {
                    try {
                        csvBase64 = JSON.parse(jobStatusPeople.resultJson);
                    } catch (e) {
                        console.error('Errore nel parsing dei risultati:', e);
                        csvBase64 = jobStatusPeople.resultJson;
                    }
                }

                if (csvBase64) {
                    downloadCSVfromBase64(csvBase64, "cd_people_download_" + formatDate(new Date(), "dd_MM_yyyy"));
                    setSelectedIds([]);
                    handleCloseDownloadModal();
                    dispatch(addBannerWithTimeout({
                        message: "File CSV generato con successo",
                        variant: "success"
                    }));
                } else {
                    dispatch(addBannerWithTimeout({
                        message: "Nessun dato CSV trovato nella risposta",
                        variant: "error"
                    }));
                }
            } else {
                dispatch(addBannerWithTimeout({
                    message: jobStatusPeople.errorMessage || "Si è verificato un errore durante la generazione del CSV",
                    variant: "error"
                }));
            }
        } else if (isPollingErrorPeople) {
            setIsPollingPeople(false);
            setJobIdPeople(null);
            dispatch(addBannerWithTimeout({
                message: "Si è verificato un errore imprevisto durante il polling dello stato del job",
                variant: "error"
            }));
        }
    }, [jobStatusPeople, isPollingErrorPeople, dispatch]);

    const handleDownloadConfirm = async () => {
        if (contactType === ContactType.PERSON) {
            try {
                const response = await getPeopleCSV(selectedIds).unwrap();
                setJobIdPeople(response.id);
                setIsPollingPeople(true);
            } catch (error: any) {
                setSelectedIds([]);
                handleCloseDownloadModal();
                dispatch(addBannerWithTimeout({
                    message: "Si è verificato un errore durante la generazione del file CSV",
                    variant: "error"
                }));
            }
        } else {
            try {
                const response = await getCompaniesCSV(selectedIds).unwrap();
                setJobIdCompanies(response.id);
                setIsPollingCompanies(true);
            } catch (error: any) {
                setSelectedIds([]);
                handleCloseDownloadModal();
                dispatch(addBannerWithTimeout({
                    message: "Si è verificato un errore durante la generazione del file CSV",
                    variant: "error"
                }));
            }
        };
    }
    return {
        handleDownloadConfirm,
        loading: isLoading,
    };
}
